/**
 * Grade espacial simples (células em graus) para trocar buscas O(n·m) por consultas locais.
 * Resultados idênticos aos de uma varredura linear: o desempate sempre favorece o menor índice
 * de inserção, igual a um `for` que só troca o melhor com `<` estrito.
 */

/** 1° de latitude em metros, arredondado para baixo: faz o intervalo em graus ficar maior (conservador). */
const METERS_PER_DEGREE_LOWER_BOUND = 110000;
/** Acima disso a consulta cai para a varredura linear (raio enorme não ganha nada com a grade). */
const MAX_CELLS_PER_QUERY = 4096;

const cellKey = (cx, cy) => `${cx},${cy}`;

/**
 * @param {Array} items
 * @param {(item) => number} getLat
 * @param {(item) => number} getLng
 * @param {number} [cellDeg] tamanho da célula em graus (0.005 ≈ 550 m)
 */
export function buildGrid(items, getLat, getLng, cellDeg = 0.005) {
    const cells = new Map();
    const all = [];
    (items || []).forEach((item, index) => {
        const lat = Number(getLat(item));
        const lng = Number(getLng(item));
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
        const entry = { item, index, lat, lng };
        all.push(entry);
        const key = cellKey(Math.floor(lat / cellDeg), Math.floor(lng / cellDeg));
        const bucket = cells.get(key);
        if (bucket) bucket.push(entry);
        else cells.set(key, [entry]);
    });
    return { cellDeg, cells, all };
}

/** Chama `cb(entry)` para cada entrada nas células que cobrem a caixa lat±dLat, lng±dLng (graus). */
export function forEachInBox(grid, lat, lng, dLat, dLng, cb) {
    const { cellDeg, cells, all } = grid;
    const x0 = Math.floor((lat - dLat) / cellDeg);
    const x1 = Math.floor((lat + dLat) / cellDeg);
    const y0 = Math.floor((lng - dLng) / cellDeg);
    const y1 = Math.floor((lng + dLng) / cellDeg);
    if ((x1 - x0 + 1) * (y1 - y0 + 1) > MAX_CELLS_PER_QUERY) {
        for (const entry of all) cb(entry);
        return;
    }
    for (let cx = x0; cx <= x1; cx++) {
        for (let cy = y0; cy <= y1; cy++) {
            const bucket = cells.get(cellKey(cx, cy));
            if (!bucket) continue;
            for (const entry of bucket) cb(entry);
        }
    }
}

/**
 * Entrada mais próxima dentro de `radiusM` (distância dada por `distFn(entry)` em metros).
 * Equivale ao "mais próximo da lista, aceito só se ≤ raio": se o mais próximo global passa do raio,
 * nenhum candidato dentro do raio existe. Empate → menor índice.
 * @returns {{ item: *, index: number, d: number } | null}
 */
export function nearestWithin(grid, lat, lng, radiusM, distFn) {
    if (!Number.isFinite(radiusM) || radiusM <= 0) return null;
    const dLat = radiusM / METERS_PER_DEGREE_LOWER_BOUND;
    const cosLat = Math.max(Math.cos((lat * Math.PI) / 180), 0.01);
    const dLng = radiusM / (METERS_PER_DEGREE_LOWER_BOUND * cosLat);
    let best = null;
    forEachInBox(grid, lat, lng, dLat, dLng, (entry) => {
        const d = distFn(entry);
        if (!Number.isFinite(d) || d > radiusM) return;
        if (!best || d < best.d || (d === best.d && entry.index < best.index)) {
            best = { item: entry.item, index: entry.index, d };
        }
    });
    return best;
}

/**
 * Menor índice de inserção cuja posição cai em |Δlat| < eps e |Δlng| < eps (comparação estrita,
 * como o laço linear original). Retorna a entrada ou null.
 */
export function firstWithinBox(grid, lat, lng, eps) {
    let best = null;
    forEachInBox(grid, lat, lng, eps, eps, (entry) => {
        if (Math.abs(entry.lat - lat) < eps && Math.abs(entry.lng - lng) < eps) {
            if (!best || entry.index < best.index) best = entry;
        }
    });
    return best;
}

/**
 * Mais próximo sem raio máximo: expande o raio (x4) até achar candidato. O primeiro raio com acerto
 * já contém o mais próximo global (nada mais perto pode estar fora dele). Chega a cobrir a Terra
 * inteira, quando a grade vira varredura linear.
 * `distFn` pode devolver Infinity/NaN para descartar a entrada.
 */
export function nearestExpanding(grid, lat, lng, distFn, startRadiusM = 250, maxRadiusM = 2.1e7) {
    for (let radius = startRadiusM; ; radius *= 4) {
        const r = Math.min(radius, maxRadiusM);
        const hit = nearestWithin(grid, lat, lng, r, distFn);
        if (hit || r >= maxRadiusM) return hit;
    }
}
